/**
 * Campaign Universe Pack contracts (architecture §9.1, §9.4).
 *
 * Content is data, compiled offline; the runtime never asks a model for it. A pack is
 * immutable, content-addressed and shared by every player of a campaign. The build
 * refuses a pack that violates any constraint expressed here, and the lints named in
 * §9.1 step 4 — `no-forced-combat`, `peak-coverage`, `age-guard`, `key-parity`,
 * `pi-blocklist`, `license-manifest-complete`, `voice-consent` — enforce the rest.
 */
import { z } from 'zod';
import { Cover, DifficultyTier, Lighting, LocalizedString, PeakKind, RumorCategory, TimeOfDay, Weather, } from './primitives.js';
import { SceneSpec } from './scene-spec.js';
/* -------------------------------------------------------- license manifest */
/** v1 allows only these three; share-alike licences live in their own packs (§9.4). */
export const PackLicenseId = z.enum([
    'CC-BY-4.0',
    'CC0-1.0',
    'proprietary',
    'CC-BY-SA-4.0',
    'CC-BY-3.0',
    'ORC',
    'Apache-2.0',
    'MIT',
]);
export const V1_ALLOWED_LICENSES = Object.freeze([
    'CC-BY-4.0',
    'CC0-1.0',
    'proprietary',
]);
/**
 * Terms that sit on top of the copyright grant — pocket-tts's acceptable-use clause is
 * the reason this block exists, and the Licenses screen renders it beside the licence
 * id (§9.4). `license-manifest-complete` fails a build that omits it where required.
 */
export const AdditionalTerms = z.object({
    kind: z.enum(['acceptable_use', 'attribution_form', 'field_of_use']),
    source: z.string(),
    summary: z.string(),
    status: z.enum(['confirmed', 'pending', 'not_applicable']),
});
export const LicenseManifest = z.object({
    /** 'pack', 'rules', 'assets/tiles', 'voices/narrator-*' … */
    scope: z.string(),
    license: PackLicenseId,
    /** Rendered verbatim; for SRD 5.2.1 this is the paragraph the Legal page prescribes. */
    attribution: z.string(),
    derivedFrom: z.array(z.string()).default([]),
    modified: z.boolean(),
    changes: z.array(z.string()).default([]),
    additionalTerms: AdditionalTerms.optional(),
});
/** Every cloned voice state carries one; `voice-consent` fails the build without it. */
export const VoiceConsentManifest = z.object({
    voiceId: z.string(),
    speaker: z.string(),
    counterparty: z.string(),
    consentDocRef: z.string(),
    scope: z.array(z.enum(['narrator', 'npc_archetype', 'marketing'])).min(1),
    /** ISO-8601 duration, e.g. 'P5Y'. */
    term: z.string(),
    territory: z.string(),
    revocable: z.boolean(),
    revocationNoticeDays: z.number().int().min(0),
    status: z.enum(['active', 'revoking', 'revoked']).default('active'),
});
/* --------------------------------------------------------- pack manifest */
export const PackDependencies = z.record(z.string(), z.string());
export const PackBuildInfo = z.object({
    compiler: z.string(),
    sourceHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    bundleHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    /** Namespace for `createRng(hash32(seedNamespace, entityId, phase))` (§9.1). */
    seedNamespace: z.string(),
});
export const PackManifest = z.object({
    id: z.string(),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** A pack declares exactly one rules system (§5.5). */
    rulesSystem: z.string(),
    dependencies: PackDependencies.default({}),
    locales: z.array(z.enum(['en-US', 'pt-BR'])).min(1),
    defaultLocale: z.enum(['en-US', 'pt-BR']),
    licenses: z.array(LicenseManifest).min(1),
    /** Below this band, `affinity` is clamped and romance storylets are rejected (§3.3). */
    adultAgeBand: z.string().default('adult'),
    build: PackBuildInfo,
});
/* ------------------------------------------------------------- entities */
export const AgeBand = z.enum(['child', 'adolescent', 'young_adult', 'adult', 'elder']);
export const Building = z.object({
    id: z.string(),
    category: z.string(),
    /** Must resolve in `maps/`. */
    mapTemplateId: z.string(),
    capacity: z.number().int().min(0),
    residential: z.boolean(),
    district: z.string(),
    atmosphereKeys: z.array(z.string()).default([]),
    propSeeds: z
        .array(z.object({ tag: z.string(), kind: z.string(), cover: Cover }))
        .default([]),
    workplaceRoles: z.array(z.string()).default([]),
    name: LocalizedString,
});
export const RosterSeed = z.object({
    id: z.string(),
    classTemplate: z.string(),
    home: z.string(),
    workplace: z.string(),
    gender: z.enum(['f', 'm', 'n']),
    ageBand: AgeBand,
    voiceRegister: z.string(),
    faction: z.string().optional(),
});
export const CanonicalNpc = RosterSeed.extend({
    /** An `srd:` id or a pack stat block. */
    statBlock: z.string(),
    name: LocalizedString,
    appearance: LocalizedString,
    backstory: LocalizedString,
    /** Frozen two-line voice note; layer B and mood selection key off it (§9.2). */
    voiceNote: z.object({
        register: z.string(),
        tics: z.array(z.string()).default([]),
        examples: z.array(LocalizedString).max(2),
    }),
    goals: z.array(z.string()).default([]),
    /** Author-only: stripped from the client bundle and from every prompt layer. */
    secrets: z.array(z.string()).default([]),
    schedule: z.array(z.object({
        period: TimeOfDay,
        locationRef: z.string(),
        activity: z.string(),
    })),
});
/* ------------------------------------------------------- quest machines */
/** Predicates use only engine-evaluable facts (§9.1). */
export const QuestPredicate = z.lazy(() => z.union([
    z.object({ all: z.array(QuestPredicate) }),
    z.object({ any: z.array(QuestPredicate) }),
    z.object({ not: QuestPredicate }),
    z.object({ flag: z.string() }),
    z.object({ flag_unset: z.string() }),
    z.object({ intent: z.string() }),
    z.object({ location_in: z.array(z.string()) }),
    z.object({ period_in: z.array(TimeOfDay) }),
    z.object({ day_gte: z.number().int() }),
    z.object({
        rel: z.object({
            npc: z.string(),
            axis: z.enum(['trust', 'respect', 'affinity', 'fear']),
            gte: z.number().int(),
        }),
    }),
    z.object({ event: z.string(), with: z.string().optional() }),
    z.object({ quest_active: z.string() }),
    /** The named quest is at exactly this stage — how a campaign written for several sessions gates each act's scenes. */
    z.object({ quest_stage: z.object({ quest: z.string(), stage: z.string() }) }),
    z.object({ faction_tension: z.object({ a: z.string(), b: z.string(), gte: z.number() }) }),
    z.object({ location_category: z.string() }),
    z.object({ not_seen_within_days: z.number().int() }),
]));
export const QuestReward = z.object({
    gold: z.number().int().default(0),
    items: z.array(z.object({ id: z.string(), qty: z.number().int().min(1) })).default([]),
    faction: z.record(z.string(), z.number().int()).default({}),
});
/**
 * ADR-110: an errand the campaign opens when a quest reaches this stage — a person
 * who wants the player, and how hard they look. `delayMinutes` is how long they wait
 * before acting; `lastsMinutes` how long a hunt goes on before they give up.
 */
export const QuestErrand = z.object({
    npc: z.string(),
    reason: z.enum(['news', 'debt', 'summons', 'grievance', 'favour', 'warning']),
    seeking: z.enum(['chance', 'watchful', 'hunting']).default('chance'),
    brief: LocalizedString,
    delayMinutes: z.number().int().min(0).default(0),
    lastsMinutes: z.number().int().min(1).optional(),
    /** The approach opens hostile: a tension beat the player's next turn decides. */
    hostile: z.boolean().default(false),
});
export const QuestStage = z.object({
    id: z.string(),
    gmBrief: LocalizedString,
    enter: QuestPredicate.optional(),
    exits: z
        .array(z.object({
        to: z.string(),
        when: QuestPredicate,
        /** `engine` exits are world events (e.g. `CombatStarted`), never instructions. */
        via: z.enum(['quest_advance', 'engine']),
    }))
        .default([]),
    rewards: QuestReward.optional(),
    effects: z.array(z.record(z.string(), z.unknown())).default([]),
    deadlineDay: z.number().int().min(1).optional(),
    failStage: z.string().optional(),
    /** A stage flagged `milestone` is what emits `LevelUp` through the director (§5.6). */
    milestone: z.boolean().default(false),
    /** ADR-110: who comes looking once this stage is reached. */
    errands: z.array(QuestErrand).default([]),
});
export const QuestMachine = z.object({
    id: z.string(),
    giver: z.string(),
    stages: z.array(QuestStage).min(1),
});
/* ------------------------------------------------------------ storylets */
/**
 * A storylet offers; it never resolves, and it may never force combat: `effects_*`
 * carrying `start_combat`, damage, conditions or enemy spawns fails the
 * `no-forced-combat` lint. `may_lead_to` is a director hint, never executed.
 */
export const StoryletNegotiationLever = z.object({
    id: z.string(),
    kind: z.enum(['fact', 'debt', 'threat', 'good']),
    label: z.string().max(120),
    slot: z.enum(['gold', 'favour', 'information', 'time', 'risk']).optional(),
    value: z.number().int().optional(),
    hidden: z.boolean().default(false),
});
export const StoryletNegotiation = z.object({
    /** The counterparty's ref; `withLabel` names them when the world has no card for the ref. */
    with: z.string(),
    withLabel: z.string().max(80).optional(),
    stake: z.string().max(160),
    direction: z.enum(['player_receives', 'player_pays']).default('player_receives'),
    playerAsk: z.object({ slot: z.enum(['gold', 'favour', 'information', 'time', 'risk']), value: z.number().int() }),
    npcOffer: z.object({ slot: z.enum(['gold', 'favour', 'information', 'time', 'risk']), value: z.number().int() }),
    stance: z.enum(['hostile', 'guarded', 'open', 'invested']),
    patience: z.number().int().min(3).max(6),
    playerLevers: z.array(StoryletNegotiationLever).default([]),
    npcLevers: z.array(StoryletNegotiationLever).default([]),
});
export const Storylet = z.object({
    id: z.string(),
    tags: z.array(z.string()).default([]),
    prerequisites: QuestPredicate.optional(),
    salience: z.object({
        base: z.number().min(0).max(1),
        boost: z.array(z.record(z.string(), z.unknown())).default([]),
    }),
    peakKind: z.array(PeakKind).default([]),
    gmBrief: LocalizedString,
    offers: z
        .array(z.object({
        intent: z.string(),
        mayLeadTo: z.array(z.string()).default([]),
    }))
        .default([]),
    effectsOnEnter: z.array(z.record(z.string(), z.unknown())).default([]),
    /** A romance storylet carries a target and is age-guarded at build (§3.3, §9.1). */
    romance: z.boolean().default(false),
    romanceTarget: z.string().optional(),
    /** Hook storylets are what `end_session` cliffhangers point at. */
    hook: z.boolean().default(false),
    /**
     * §6.10: a `negotiation` peak carries its table — who sits across it, what is
     * on it, how the room stands, and the levers each side holds. The engine opens
     * the table from this when the player engages the storylet with a spoken
     * skill; nothing about it is decided at run time.
     */
    negotiation: StoryletNegotiation.optional(),
});
/** `peak-coverage`: ≥ 6 storylets per selectable peak kind, none above 45% (§9.1). */
export const PEAK_COVERAGE = Object.freeze({ minPerKind: 6, maxShareOfPool: 0.45 });
/* ------------------------------------------------------ encounter tables */
export const EncounterResult = z.union([
    z.literal('none'),
    z.object({
        encounter: z.enum(['sighting', 'traces', 'ambush', 'travellers', 'patrol']),
        statBlock: z.string().optional(),
        count: z.tuple([z.number().int().min(1), z.number().int().min(1)]).optional(),
        disposition: z.enum(['hostile', 'defensive', 'fleeing', 'bystander']).optional(),
        intent: z
            .enum(['hold_chokepoint', 'focus_weakest', 'flank', 'guard_npc', 'flee_when_bloodied'])
            .optional(),
        surprise: z
            .enum(['none', 'player_surprises', 'player_surprised', 'contested_stealth'])
            .optional(),
        storylet: z.string().optional(),
    }),
]);
export const EncounterTable = z.object({
    id: z.string(),
    terrain: z.string(),
    tier: z.number().int().min(0).max(4),
    /** Callable only from travel and trespass predicates (`encounter-callers` lint). */
    trigger: z.object({
        travel: z.object({ dangerGte: z.number().min(0).max(1) }).optional(),
        trespass: z.object({ factionTerritory: z.boolean() }).optional(),
    }),
    roll: z.enum(['d20', 'd100']),
    entries: z
        .array(z.object({
        range: z.tuple([z.number().int().min(1), z.number().int().min(1)]),
        result: EncounterResult,
        /** Compared against `encounterBudget()` *before* the roll (§6.1 path 3). */
        tier: z.number().int().min(0).max(4),
    }))
        .min(1),
    threatBudgetCap: z.literal('from_director'),
});
/* --------------------------------------------------------- map templates */
export const MapTemplate = z.object({
    id: z.string(),
    size: z.object({
        w: z.number().int().min(8).max(24),
        h: z.number().int().min(6).max(18),
    }),
    setting: z.enum(['indoor', 'outdoor', 'underground']),
    defaultTerrain: z.string(),
    palette: z.string(),
    walls: z
        .array(z.object({
        x: z.number().int(),
        y: z.number().int(),
        w: z.number().int().min(1),
        h: z.number().int().min(1),
        t: z.string().default('wall'),
    }))
        .default([]),
    fixedProps: z
        .array(z.object({
        id: z.string(),
        kind: z.string(),
        at: z.object({ x: z.number().int(), y: z.number().int() }),
        size: z.number().int().min(1).max(3).default(1),
        cover: Cover.default('none'),
        blocksMove: z.boolean().default(true),
        blocksSight: z.boolean().default(false),
        slot: z.string().optional(),
    }))
        .default([]),
    exits: z
        .array(z.object({
        id: z.string(),
        at: z.object({ x: z.number().int(), y: z.number().int() }),
        kind: z.enum(['door', 'gate', 'stairs', 'open', 'window']),
        leadsTo: z.string(),
    }))
        .min(1)
        .max(4),
    spawnZones: z.object({
        player: z.array(z.object({ x: z.number().int(), y: z.number().int() })).min(1),
        hostile: z.array(z.object({ x: z.number().int(), y: z.number().int() })).min(1),
        defensive: z.array(z.object({ x: z.number().int(), y: z.number().int() })).default([]),
        neutral: z.array(z.object({ x: z.number().int(), y: z.number().int() })).default([]),
    }),
    /** Presence tokens land at the anchor matching their current activity tag. */
    bystanderAnchors: z
        .array(z.object({
        activityTag: z.string(),
        cell: z.object({ x: z.number().int(), y: z.number().int() }),
    }))
        .default([]),
    variants: z
        .record(z.string(), z.object({
        lighting: Lighting.optional(),
        weather: Weather.optional(),
        addProps: z.array(z.string()).default([]),
        removeProps: z.array(z.string()).default([]),
    }))
        .default({}),
    name: LocalizedString,
});
/* --------------------------------------------------- misc pack contracts */
export const SiftPattern = z.object({
    id: z.string(),
    predicate: QuestPredicate,
    hookKey: z.string(),
    salienceTerms: z.array(z.string()).default([]),
});
export const RumorTemplate = z.object({
    id: z.string(),
    category: RumorCategory,
    claimTemplateKey: z.string(),
    slots: z.array(z.string()).default([]),
    /** Per-hop mutation graph: hop index → the template key it degrades into. */
    hopMutations: z.record(z.string(), z.string()).default({}),
    reliability: z.enum(['certain', 'likely', 'doubtful', 'false']),
});
export const GlossaryTerm = z.object({
    id: z.string(),
    en: z.string(),
    'pt-BR': z.string(),
    synonyms: z.array(z.string()).default([]),
    /** pocket-tts pronunciation hint: "CA" → "cê-á". */
    say: z.string().optional(),
});
/** One entry of `pi-blocklist.json`; the runtime filter blocks, it does not flag. */
export const PiBlocklistEntry = z.object({
    stem: z.string(),
    locale: z.enum(['en-US', 'pt-BR', 'both']),
    category: z.enum([
        'settings',
        'deities_and_characters',
        'excluded_creatures',
        'non_srd_subclasses_spells_feats',
        'creator_named_spells',
        'renamed_items',
        'trademarks',
    ]),
    /** Absent for trademarks: a hit is a hard failure, and the clause is dropped. */
    replacement: z.string().optional(),
});
/** A difficulty tier a pack may attach to an authored check. */
export const PackCheckTier = DifficultyTier;
/** A map template rasterised for a specific scene is exactly a `SceneSpec`. */
export const PackSceneFixture = SceneSpec;
/* ---------------------------------------------------- starting points */
/**
 * Where a campaign's first session may open (ADR-076). A small authored set —
 * three to five — from which the selector picks for the character; never
 * invented at run time. `hookStoryletId` names the storylet the opening plays
 * toward, and its tags are what a player's lines are checked against.
 */
export const StartingPoint = z.object({
    id: z.string(),
    locationRef: z.string(),
    day: z.number().int().min(1),
    period: TimeOfDay,
    hookStoryletId: z.string(),
    gmBrief: LocalizedString,
    /** A `QuestPredicate` over the character, when the pack wants a fit; absent means any. */
    fitsWhen: QuestPredicate.optional(),
    /** What the lobby shows when a campaign has more than one opening to choose from. */
    label: LocalizedString.optional(),
    /** The objective line the HUD opens with; absent means the pack's default. */
    objective: LocalizedString.optional(),
    /** Quest stages in force at this opening (`questId: stageId`); absent means every campaign quest at its hook. */
    quests: z.record(z.string(), z.string()).optional(),
    /** Which session of the campaign this opening is written for — an act opener carries what came before in its brief. */
    sessionNumber: z.number().int().min(1).optional(),
});
//# sourceMappingURL=pack.js.map